<!--
  App — orquestrador do fluxo completo (Wave 12).
  State machine: auth → privacy → import → assumptions → analyzing → dashboard | error
-->
<script lang="ts">
  import { onDestroy } from 'svelte';
  import { signOut } from 'firebase/auth';
  import { appStore } from './app/store';
  import { authStore } from './features/auth/authStore';
  import { signInWithPontotelDomain } from './features/auth/signInWithPontotelDomain';
  import { getFirebaseAuth } from './features/auth/firebaseConfig';
  import { analyzeFilesInWorker, INITIAL_WORKER_STATE } from './workers/workerClient';
  import LoginScreen from './features/auth/LoginScreen.svelte';
  import LogoutButton from './features/auth/LogoutButton.svelte';
  import PrivacyNotice from './features/privacy/PrivacyNotice.svelte';
  import ImportStep from './features/import/ImportStep.svelte';
  import AssumptionsForm from './features/assumptions/AssumptionsForm.svelte';
  import AnalysisProgress from './features/analysis/AnalysisProgress.svelte';
  import DashboardLayout from './features/dashboard/DashboardLayout.svelte';
  import Button from './components/Button.svelte';
  import Stepper from './components/Stepper.svelte';
  import { APP_NAME, APP_TAGLINE } from './app/constants';

  const { state, acceptPrivacy, setCurrentStep, setAnalysisProgress, setResult, clearSession } = appStore;
  const auth = authStore;
  let cancelAnalysis: (() => void) | null = null;

  const STEPS = [
    { id: 'import', label: 'Importar arquivos' },
    { id: 'assumptions', label: 'Premissas' },
    { id: 'analyzing', label: 'Análise' },
    { id: 'dashboard', label: 'Resultado' },
  ];

  $: if ($auth.status === 'signedIn' && $state.currentStep === 'auth') {
    setCurrentStep($state.privacyAccepted ? 'import' : 'privacy');
  }
  $: if ($auth.status === 'signedIn' && !$state.privacyAccepted && $state.currentStep !== 'privacy' && $state.currentStep !== 'auth') {
    setCurrentStep('privacy');
  }
  $: if ($auth.status === 'signedIn' && $state.privacyAccepted && $state.currentStep === 'privacy') {
    setCurrentStep('import');
  }

  async function handleSignIn() {
    try {
      const user = await signInWithPontotelDomain({ auth: getFirebaseAuth() });
      authStore.setUser(user);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao autenticar';
      authStore.setError(msg);
    }
  }

  async function handleLogout() {
    await signOut(getFirebaseAuth());
    authStore.setUser(null);
    setCurrentStep('auth');
  }

  function goToAssumptions() {
    if ($state.loadedFiles.length === 0) return;
    setCurrentStep('assumptions');
  }

  function goToImport() {
    setCurrentStep('import');
  }

  async function startAnalysis() {
    if ($state.loadedFiles.length === 0) return;
    setCurrentStep('analyzing');
    setAnalysisProgress({ ...INITIAL_WORKER_STATE });

    const afdFiles = $state.loadedFiles.filter(f => f.kind === 'afd');
    const input = {
      afdFiles,
      assumptions: $state.assumptions,
    };

    const { promise, cancel } = analyzeFilesInWorker(input, {
      onProgress: (s) => setAnalysisProgress(s),
    });
    cancelAnalysis = cancel;

    try {
      const result = await promise;
      setResult(result);
      setCurrentStep('dashboard');
    } catch (err) {
      setAnalysisProgress({
        step: 'error',
        percent: 0,
        label: 'Erro na análise',
        error: err instanceof Error ? err.message : 'Erro desconhecido',
      });
      setCurrentStep('error');
    } finally {
      cancelAnalysis = null;
    }
  }

  function handleCancelAnalysis() {
    if (cancelAnalysis) cancelAnalysis();
    setCurrentStep('import');
  }

  function handleClearSession() {
    clearSession();
    setCurrentStep('import');
  }

  onDestroy(() => { if (cancelAnalysis) cancelAnalysis(); });
</script>

{#if $auth.status === 'checking'}
  <div class="auth-checking" role="status" aria-live="polite">Carregando…</div>
{:else if $auth.status !== 'signedIn'}
  <LoginScreen status={$auth.status} error={$auth.error} onSignIn={handleSignIn} />
{:else if $state.currentStep === 'privacy' || (!$state.privacyAccepted && $state.currentStep !== 'auth')}
  <PrivacyNotice onAccept={acceptPrivacy} />
{:else}
  <main class="app-shell">
    <header class="app-header">
      <div class="title-row">
        <div>
          <h1>{APP_NAME}</h1>
          <p class="tagline">{APP_TAGLINE}</p>
        </div>
        {#if $auth.user}
          <LogoutButton displayName={$auth.user.displayName} email={$auth.user.email} onLogout={handleLogout} />
        {/if}
      </div>
    </header>

    {#if $state.currentStep !== 'dashboard' && $state.currentStep !== 'error'}
      <div class="stepper-wrap">
        <Stepper steps={STEPS} currentStep={$state.currentStep} />
      </div>
    {/if}

    <section class="app-content" aria-label="Conteúdo principal">
      {#if $state.currentStep === 'import'}
        <ImportStep />
        {#if $state.loadedFiles.length > 0}
          <div class="cta-row">
            <Button variant="primary" on:click={goToAssumptions}>Próximo: configurar premissas</Button>
          </div>
        {/if}
      {:else if $state.currentStep === 'assumptions'}
        <AssumptionsForm />
        <div class="cta-row">
          <Button variant="ghost" on:click={goToImport}>Voltar</Button>
          <Button variant="primary" on:click={startAnalysis}>Iniciar análise</Button>
        </div>
      {:else if $state.currentStep === 'analyzing'}
        <AnalysisProgress state={$state.analysisProgress} on:cancel={handleCancelAnalysis} />
      {:else if $state.currentStep === 'dashboard' && $state.result}
        <DashboardLayout result={$state.result} on:clearSession={handleClearSession} />
      {:else if $state.currentStep === 'error'}
        <div class="error-panel">
          <h2>Ocorreu um problema na análise</h2>
          <p>{$state.analysisProgress?.error ?? 'Erro desconhecido'}</p>
          <Button variant="primary" on:click={handleClearSession}>Recomeçar</Button>
        </div>
      {/if}
    </section>
  </main>
{/if}

<style>
  .auth-checking {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--ptt-gray-300, #323232);
    color: var(--ptt-white, #fff);
    font-family: var(--ptt-font-sans, "Hanken Grotesk", Arial, sans-serif);
    z-index: 200;
  }

  .app-shell {
    font-family: var(--ptt-font-sans, "Hanken Grotesk", Arial, sans-serif);
    max-width: 1100px;
    margin: 0 auto;
    padding: var(--ptt-space-6, 2rem) var(--ptt-space-5, 1.5rem);
    color: var(--ptt-gray-300, #323232);
  }

  .app-header {
    margin-bottom: var(--ptt-space-7, 3rem);
  }

  .title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--ptt-space-4, 16px);
  }

  .app-header h1 {
    margin: 0 0 0.25rem;
    font-size: 1.75rem;
    color: var(--ptt-black, #000);
  }

  .tagline {
    margin: 0;
    color: var(--ptt-gray-200, #565656);
  }

  .stepper-wrap {
    margin-bottom: var(--ptt-space-6, 2rem);
  }

  .cta-row {
    display: flex;
    gap: var(--ptt-space-3, 12px);
    justify-content: flex-end;
    margin-top: var(--ptt-space-5, 1.5rem);
  }

  .error-panel {
    padding: var(--ptt-space-6, 2rem);
    background: var(--ptt-yellow-10, #FEF3CF);
    border-left: 4px solid var(--ptt-orange-100, #FB7224);
    border-radius: var(--ptt-radius-md, 8px);
  }

  .error-panel h2 {
    margin-top: 0;
    color: var(--ptt-gray-300, #323232);
  }
</style>
